import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SidebarInfoCard } from '@/components/ui/SidebarInfoCard';
import { Button } from '@/components/ui/Button';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import styles from './styles.module.scss';

export interface CoefficientsCardProps {
  /** Team 1 name (short version preferred) */
  team1Name: string;
  /** Team 2 name (short version preferred) */
  team2Name: string;
  /** Team 1 win coefficient */
  team1Coefficient?: number | string | null;
  /** Draw coefficient */
  drawCoefficient?: number | string | null;
  /** Team 2 win coefficient */
  team2Coefficient?: number | string | null;
  /** Coefficient source ('api', 'manual', 'none') */
  source?: string;
  /** Bookmaker name for team1 win odds */
  team1Bookmaker?: string | null;
  /** Bookmaker name for draw odds */
  drawBookmaker?: string | null;
  /** Bookmaker name for team2 win odds */
  team2Bookmaker?: string | null;
  /** Show edit button */
  showEditButton?: boolean;
  /** Edit button click handler (desktop popup mode) */
  onEditClick?: () => void;
  /** Inline save handler (mobile inline mode) */
  onInlineSave?: (team1: number, draw: number, team2: number) => Promise<void>;
  /** Start in edit mode immediately (skip preview) — parity with PredictionCard.initialEditMode */
  initialEditMode?: boolean;
  /** Called when user cancels/closes the inline form — lets parent return to previous view */
  onCancel?: () => void;
  /** Render bare edit-mode body (no `.card` wrapper) when the parent provides its own card chrome — used by MatchDetailCard's unified mobile/tablet layout for form takeover. Mobile + isEditing only. */
  noChrome?: boolean;
  /** Additional className */
  className?: string;
}

// Check if coefficient is a "not set" default value (null, undefined, 1.0, '1.0', '1', 1)
const isDefaultCoefficient = (val: number | string | null | undefined): boolean => {
  if (val === undefined || val === null) return true;
  const numVal = parseFloat(String(val));
  return isNaN(numVal) || numVal === 1 || numVal === 1.0;
};

// Convert coefficient to edit value - empty string if default, otherwise the value
const toEditValue = (val: number | string | null | undefined): string => {
  if (isDefaultCoefficient(val)) return '';
  return String(val);
};

/**
 * Coefficients card for match sidebar.
 * Shows betting coefficients for team1 win, draw, and team2 win.
 *
 * Layout modes:
 * - Desktop (≥lg): wrapped in SidebarInfoCard chrome
 * - Mobile/tablet (<lg): own .card + .main + .footer anatomy mirroring MatchDetailCard
 *
 * Edit modes:
 * - Mobile (<lg) with onInlineSave: inline edit
 * - Desktop (≥lg) with onEditClick: popup edit
 */
export const CoefficientsCard: React.FC<CoefficientsCardProps> = ({
  team1Name,
  team2Name,
  team1Coefficient = '1.0',
  drawCoefficient = '1.0',
  team2Coefficient = '1.0',
  source,
  team1Bookmaker,
  drawBookmaker,
  team2Bookmaker,
  showEditButton = false,
  onEditClick,
  onInlineSave,
  initialEditMode = false,
  onCancel,
  noChrome = false,
  className,
}) => {
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation(['tournament', 'common']);
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Scroll form into view when editing starts
  const scrollFormIntoView = useCallback(() => {
    if (!formRef.current) return;

    // Small delay to ensure form is rendered
    setTimeout(() => {
      formRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);
  }, []);

  // Scroll into view when editing mode activates
  useEffect(() => {
    if (isEditing) {
      scrollFormIntoView();
    }
  }, [isEditing, scrollFormIntoView]);

  // Form state
  const [editTeam1, setEditTeam1] = useState(toEditValue(team1Coefficient));
  const [editDraw, setEditDraw] = useState(toEditValue(drawCoefficient));
  const [editTeam2, setEditTeam2] = useState(toEditValue(team2Coefficient));

  // Reset form when coefficients change externally
  useEffect(() => {
    if (!isEditing) {
      setEditTeam1(toEditValue(team1Coefficient));
      setEditDraw(toEditValue(drawCoefficient));
      setEditTeam2(toEditValue(team2Coefficient));
    }
  }, [team1Coefficient, drawCoefficient, team2Coefficient, isEditing]);

  const handleEditClick = () => {
    if (isMobile && onInlineSave) {
      // Mobile: switch to inline edit mode
      setIsEditing(true);
      setError(null);
    } else if (onEditClick) {
      // Desktop: open popup
      onEditClick();
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    // Reset to original values
    setEditTeam1(toEditValue(team1Coefficient));
    setEditDraw(toEditValue(drawCoefficient));
    setEditTeam2(toEditValue(team2Coefficient));
    if (onCancel) onCancel();
  };

  const validateCoefficient = (value: string): number | null => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 1.01) {
      return null;
    }
    return num;
  };

  const handleSave = async () => {
    setError(null);

    const t1 = validateCoefficient(editTeam1);
    const d = validateCoefficient(editDraw);
    const t2 = validateCoefficient(editTeam2);

    if (t1 === null || d === null || t2 === null) {
      setError(t('tournament:coefficientsMinError'));
      return;
    }

    if (!onInlineSave) return;

    setIsSubmitting(true);
    try {
      await onInlineSave(t1, d, t2);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || t('tournament:errorSaving'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const headerAction = showEditButton ? (
    <button className={styles.editIconBtn} onClick={handleEditClick} aria-label={t('tournament:editCoefficients')}>
      <i className="icon-pen" />
    </button>
  ) : undefined;

  // Whether all 3 coefficient values are at their default (treated as "not set")
  const hasNoValues =
    isDefaultCoefficient(team1Coefficient) &&
    isDefaultCoefficient(drawCoefficient) &&
    isDefaultCoefficient(team2Coefficient);

  // Single-bookmaker source detection: when all three values come from the same bookmaker we
  // collapse the per-cell bookmaker labels into one footer pill (cleaner on mobile).
  const apiBookmakerCommon = source === 'api'
    ? [team1Bookmaker, drawBookmaker, team2Bookmaker].filter(Boolean).find(b => !!b) || null
    : null;

  // Inline edit form body (shared between mobile chrome and SidebarInfoCard wrapper)
  const editFormBody = (
    <div className={styles.editForm} ref={formRef}>
      <div className={styles.coefficientsGrid}>
        <div className={styles.coefficientItem}>
          <label className={styles.coefficientLabel}>{team1Name}</label>
          <input
            type="number"
            step="0.01"
            min="1.01"
            value={editTeam1}
            onChange={(e) => setEditTeam1(e.target.value)}
            className={`${styles.coefficientInput} ${styles.team1Input}`}
            disabled={isSubmitting}
          />
        </div>
        <div className={styles.coefficientItem}>
          <label className={styles.coefficientLabel}>{t('tournament:draw')}</label>
          <input
            type="number"
            step="0.01"
            min="1.01"
            value={editDraw}
            onChange={(e) => setEditDraw(e.target.value)}
            className={`${styles.coefficientInput} ${styles.drawInput}`}
            disabled={isSubmitting}
          />
        </div>
        <div className={styles.coefficientItem}>
          <label className={styles.coefficientLabel}>{team2Name}</label>
          <input
            type="number"
            step="0.01"
            min="1.01"
            value={editTeam2}
            onChange={(e) => setEditTeam2(e.target.value)}
            className={`${styles.coefficientInput} ${styles.team2Input}`}
            disabled={isSubmitting}
          />
        </div>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      <div className={styles.editActions}>
        <Button variant="outlined" size="sm" onClick={handleCancel} disabled={isSubmitting}>
          {t('common:cancel')}
        </Button>
        <Button variant="solid" size="sm" onClick={handleSave} disabled={isSubmitting}>
          {t('common:save')}
        </Button>
      </div>
    </div>
  );

  // Render the 3-column grid (display mode), shared by both chromes.
  const renderDisplayGrid = (heavyValueClass: string) => (
    <div className={styles.coefficientsGrid}>
      <div className={styles.coefficientItem}>
        <span className={styles.coefficientLabel}>{team1Name}</span>
        <span className={`${heavyValueClass} ${isDefaultCoefficient(team1Coefficient) ? styles.coefficientValueEmptyLg : ''}`}>
          {isDefaultCoefficient(team1Coefficient) ? '—' : team1Coefficient}
        </span>
        {/* Per-cell bookmaker shown only on desktop chrome (mobile has a single source pill in footer) */}
      </div>
      <div className={styles.coefficientItem}>
        <span className={styles.coefficientLabel}>{t('tournament:draw')}</span>
        <span className={`${heavyValueClass} ${isDefaultCoefficient(drawCoefficient) ? styles.coefficientValueEmptyLg : ''}`}>
          {isDefaultCoefficient(drawCoefficient) ? '—' : drawCoefficient}
        </span>
      </div>
      <div className={styles.coefficientItem}>
        <span className={styles.coefficientLabel}>{team2Name}</span>
        <span className={`${heavyValueClass} ${isDefaultCoefficient(team2Coefficient) ? styles.coefficientValueEmptyLg : ''}`}>
          {isDefaultCoefficient(team2Coefficient) ? '—' : team2Coefficient}
        </span>
      </div>
    </div>
  );

  // ============================================================
  // MOBILE / TABLET CHROME (< lg) — own .card + .main + .footer
  // ============================================================
  if (isMobile) {
    if (isEditing) {
      const editBody = (
        <div className={styles.main}>
          <div className={styles.captionRow}>
            <span className={styles.caption}>{t('tournament:coefficients')}</span>
            <button
              className={styles.editIconBtn}
              onClick={handleCancel}
              aria-label={t('tournament:closeEditForm')}
            >
              <i className="icon-xmark" />
            </button>
          </div>
          {editFormBody}
        </div>
      );
      return noChrome ? editBody : <div className={`${styles.card} ${className || ''}`}>{editBody}</div>;
    }

    // Display mode
    return (
      <div className={`${styles.card} ${className || ''}`}>
        <div className={styles.main}>
          <div className={styles.captionRow}>
            <span className={styles.caption}>{t('tournament:coefficients')}</span>
            {showEditButton && !hasNoValues && (
              <button
                className={styles.editIconBtn}
                onClick={handleEditClick}
                aria-label={t('tournament:editCoefficients')}
              >
                <i className="icon-pen" />
              </button>
            )}
          </div>
          {renderDisplayGrid(styles.coefficientValueLg)}
        </div>

        {/* Footer: CTA when coeffs are missing AND user can set them; otherwise source / status */}
        {hasNoValues && showEditButton ? (
          <div className={styles.footerCta}>
            <Button variant="solid" color="accent" size="md" onClick={handleEditClick}>
              {t('tournament:setCoefficientsCta')}
            </Button>
          </div>
        ) : (
          <div className={styles.footer}>
            <span className={styles.footerLabel}>{t('tournament:coefficients')}</span>
            {hasNoValues ? (
              <span className={styles.footerValue}>{t('tournament:notSetCoefficients')}</span>
            ) : apiBookmakerCommon ? (
              <span className={styles.sourcePill}>{apiBookmakerCommon}</span>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  // ============================================================
  // DESKTOP CHROME (≥ lg) — SidebarInfoCard wrapper as before
  // ============================================================
  if (isEditing) {
    return (
      <SidebarInfoCard
        title={t('tournament:coefficients')}
        headerAction={
          <button className={styles.editIconBtn} onClick={handleCancel} aria-label="Close edit form">
            <i className="icon-xmark" />
          </button>
        }
        className={className}
      >
        {editFormBody}
      </SidebarInfoCard>
    );
  }

  return (
    <SidebarInfoCard
      title={t('tournament:coefficients')}
      headerAction={headerAction}
      className={className}
    >
      <div className={styles.coefficientsGrid}>
        <div className={styles.coefficientItem}>
          <span className={styles.coefficientLabel}>{team1Name}</span>
          <span className={`${styles.coefficientValue} ${styles.team1}`}>
            {isDefaultCoefficient(team1Coefficient) ? '—' : team1Coefficient}
          </span>
          {source === 'api' && team1Bookmaker && (
            <span className={styles.bookmakerLabel}>{team1Bookmaker}</span>
          )}
        </div>
        <div className={styles.coefficientItem}>
          <span className={styles.coefficientLabel}>{t('tournament:draw')}</span>
          <span className={`${styles.coefficientValue} ${styles.draw}`}>
            {isDefaultCoefficient(drawCoefficient) ? '—' : drawCoefficient}
          </span>
          {source === 'api' && drawBookmaker && (
            <span className={styles.bookmakerLabel}>{drawBookmaker}</span>
          )}
        </div>
        <div className={styles.coefficientItem}>
          <span className={styles.coefficientLabel}>{team2Name}</span>
          <span className={`${styles.coefficientValue} ${styles.team2}`}>
            {isDefaultCoefficient(team2Coefficient) ? '—' : team2Coefficient}
          </span>
          {source === 'api' && team2Bookmaker && (
            <span className={styles.bookmakerLabel}>{team2Bookmaker}</span>
          )}
        </div>
      </div>
    </SidebarInfoCard>
  );
};

export default CoefficientsCard;
